import { z } from 'zod';

import {
  AI_VISIBILITY_PLATFORM_COMPARISON_STATUS,
  AI_VISIBILITY_PLATFORMS,
  AI_VISIBILITY_RUN_STATUS,
  AI_VISIBILITY_SCHEDULE_FREQUENCY,
  AI_VISIBILITY_SCHEDULE_MONTH_DAYS,
  AI_VISIBILITY_SCHEDULE_TIME_SLOTS,
  ISO_WEEKDAYS,
  REPORT_SCHEDULE_LAST_DAY_OF_MONTH
} from '../dictionary';

/** Unique UUID identifier of an AI Visibility report, validated per the API docs format. */
export const AiVisibilityReportIdSchema = z.string().uuid();
export type AiVisibilityReportId = z.infer<typeof AiVisibilityReportIdSchema>;

/** Unique UUID identifier of an AI Visibility report run, validated per the API docs format. */
export const AiVisibilityRunIdSchema = z.string().uuid();
export type AiVisibilityRunId = z.infer<typeof AiVisibilityRunIdSchema>;

/** Zod schema validating an AI platform identifier against the platform dictionary. */
export const AiVisibilityPlatformSchema = z.nativeEnum(AI_VISIBILITY_PLATFORMS);
/** Union of AI platform identifiers, e.g. "google_ai_overview" | "chatgpt". */
export type AiVisibilityPlatform = z.infer<typeof AiVisibilityPlatformSchema>;

/** Zod schema validating a 3-hour schedule slot start time (HH:MM:SS). */
export const AiVisibilityScheduleTimeSlotSchema = z.enum(AI_VISIBILITY_SCHEDULE_TIME_SLOTS);
/** 3-hour schedule slot start time (HH:MM:SS) accepted by the AI Visibility API. */
export type AiVisibilityScheduleTimeSlot = z.infer<typeof AiVisibilityScheduleTimeSlotSchema>;
/** Zod schema validating an AI Visibility run status against the status dictionary. */
export const AiVisibilityRunStatusSchema = z.nativeEnum(AI_VISIBILITY_RUN_STATUS);
/** Union of AI Visibility run status values, e.g. "pending" | "completed". */
export type AiVisibilityRunStatus = z.infer<typeof AiVisibilityRunStatusSchema>;

/** Zod schema validating a platform comparison status against the status dictionary. */
export const AiVisibilityPlatformComparisonStatusSchema = z.nativeEnum(AI_VISIBILITY_PLATFORM_COMPARISON_STATUS);
/** Union of platform comparison status values, e.g. "compared" | "new". */
export type AiVisibilityPlatformComparisonStatus = z.infer<typeof AiVisibilityPlatformComparisonStatusSchema>;

/** Zod schema validating a report schedule frequency against the frequency dictionary. */
export const AiVisibilityScheduleFrequencySchema = z.nativeEnum(AI_VISIBILITY_SCHEDULE_FREQUENCY);
/** Union of report schedule frequency values, e.g. "manual" | "weekly". */
export type AiVisibilityScheduleFrequency = z.infer<typeof AiVisibilityScheduleFrequencySchema>;

/** Zod schema validating the `schedule_run_on` of a weekly schedule — an ISO weekday 1-7. */
export const AiVisibilityScheduleWeekdaySchema = z.nativeEnum(ISO_WEEKDAYS);

/** Zod schema validating the `schedule_run_on` of a monthly schedule — a day 1-28, or -1 for the last day. */
export const AiVisibilityScheduleMonthDaySchema = z.union([
  z.number().int().min(1).max(AI_VISIBILITY_SCHEDULE_MONTH_DAYS.length),
  z.literal(REPORT_SCHEDULE_LAST_DAY_OF_MONTH)
]);
